import React, { useEffect, useState } from 'react';
import { BedDouble, Users, Calendar, Activity, BarChart3, Building2, Plus, RefreshCw } from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import { Card, LoadingState, Badge, Button, Modal, Input, Select, EmptyState } from '../../components/common/UI';
import { bedsAPI, analyticsAPI } from '../../services/api';
import toast from 'react-hot-toast';

const NAV_ITEMS = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: Activity },
  { path: '/admin/users', label: 'User Management', icon: Users },
  { path: '/admin/appointments', label: 'Appointments', icon: Calendar },
  { path: '/admin/beds', label: 'Bed Management', icon: BedDouble },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/centers', label: 'Centers', icon: Building2 },
];

const BED_STATUS_CONFIG = {
  available: { color: 'bg-emerald-100 border-emerald-300 text-emerald-700', dot: 'bg-emerald-500', label: 'Available' },
  occupied: { color: 'bg-red-100 border-red-300 text-red-700', dot: 'bg-red-500', label: 'Occupied' },
  maintenance: { color: 'bg-amber-100 border-amber-300 text-amber-700', dot: 'bg-amber-500', label: 'Maintenance' },
  reserved: { color: 'bg-blue-100 border-blue-300 text-blue-700', dot: 'bg-blue-500', label: 'Reserved' },
};

export default function AdminBeds() {
  const [beds, setBeds] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBed, setSelectedBed] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [updating, setUpdating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newBed, setNewBed] = useState({ bedNumber: '', center: '', room: '', floor: '', type: 'therapy' });

  const load = () => {
    Promise.all([bedsAPI.getAll(), analyticsAPI.getCenters()])
      .then(([bedRes, centerRes]) => {
        setBeds(bedRes.data.beds || []);
        setCenters(centerRes.data.centers || []);
        setNewBed(f => ({ ...f, center: centerRes.data.centers?.[0]?._id || '' }));
      })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const updateBedStatus = async (bedId, status) => {
    setUpdating(true);
    try {
      await bedsAPI.update(bedId, { status });
      setBeds(prev => prev.map(b => b._id === bedId ? { ...b, status } : b));
      setSelectedBed(prev => prev ? { ...prev, status } : null);
      toast.success(`Bed status updated to ${status}`);
    } catch { toast.error('Failed to update bed status'); }
    finally { setUpdating(false); }
  };

  const createBed = async () => {
    if (!newBed.bedNumber || !newBed.center) return toast.error('Fill required fields');
    setSaving(true);
    try {
      await bedsAPI.create(newBed);
      toast.success('Bed created!');
      setShowCreate(false);
      load();
    } catch { toast.error('Failed to create bed'); }
    finally { setSaving(false); }
  };

  const filtered = statusFilter ? beds.filter(b => b.status === statusFilter) : beds;
  const counts = {
    available: beds.filter(b => b.status === 'available').length,
    occupied: beds.filter(b => b.status === 'occupied').length,
    maintenance: beds.filter(b => b.status === 'maintenance').length,
    reserved: beds.filter(b => b.status === 'reserved').length,
  };

  if (loading) return <DashboardLayout navItems={NAV_ITEMS} role="admin"><LoadingState /></DashboardLayout>;

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="admin">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Bed Management</h1>
          <p className="text-gray-500 text-sm">{beds.length} total beds across all centers</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4" /> Refresh</Button>
          <Button onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" /> Add Bed</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {Object.entries(counts).map(([status, count]) => {
          const cfg = BED_STATUS_CONFIG[status];
          return (
            <button key={status} onClick={() => setStatusFilter(statusFilter === status ? '' : status)}
              className={`p-4 rounded-2xl border-2 text-left transition-all hover:shadow-md ${statusFilter === status ? cfg.color + ' shadow-md' : 'bg-white border-gray-100'}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-3 h-3 rounded-full ${cfg.dot}`} />
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{cfg.label}</span>
              </div>
              <p className="text-3xl font-bold text-gray-800">{count}</p>
            </button>
          );
        })}
      </div>

      {/* Occupancy Bar */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
        <div className="flex justify-between mb-2">
          <p className="text-sm font-semibold text-gray-700">Bed Occupancy Overview</p>
          <p className="text-sm text-gray-500">{beds.length > 0 ? Math.round((counts.occupied / beds.length) * 100) : 0}% occupied</p>
        </div>
        <div className="flex h-6 rounded-xl overflow-hidden gap-0.5">
          {Object.entries(counts).map(([status, count]) => {
            const pct = beds.length > 0 ? (count / beds.length) * 100 : 0;
            const colors = { available: 'bg-emerald-500', occupied: 'bg-red-500', maintenance: 'bg-amber-500', reserved: 'bg-blue-500' };
            return pct > 0 ? <div key={status} className={`${colors[status]} transition-all`} style={{ width: `${pct}%` }} title={`${BED_STATUS_CONFIG[status].label}: ${count}`} /> : null;
          })}
        </div>
        <div className="flex flex-wrap gap-4 mt-3">
          {Object.entries(BED_STATUS_CONFIG).map(([status, cfg]) => (
            <div key={status} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
              <span className="text-xs text-gray-500">{cfg.label}: {counts[status]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Visual Bed Grid */}
      <Card title="Bed Grid View" subtitle="Click a bed to manage">
        {filtered.length === 0 ? (
          <EmptyState icon={BedDouble} title="No beds found" />
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
            {filtered.map(bed => {
              const cfg = BED_STATUS_CONFIG[bed.status] || BED_STATUS_CONFIG.available;
              return (
                <button key={bed._id} onClick={() => setSelectedBed(bed)}
                  className={`relative p-3 rounded-xl border-2 text-center transition-all hover:shadow-md hover:-translate-y-0.5 ${cfg.color}`}>
                  <div className="text-lg mb-1">🛏️</div>
                  <p className="text-xs font-bold leading-tight">{bed.bedNumber}</p>
                  <p className="text-xs opacity-70 truncate">{bed.room}</p>
                  <div className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${cfg.dot}`} />
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {/* Bed Detail Modal */}
      <Modal isOpen={!!selectedBed} onClose={() => setSelectedBed(null)} title={`Bed ${selectedBed?.bedNumber}`} size="sm">
        {selectedBed && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                ['Bed Number', selectedBed.bedNumber],
                ['Room', selectedBed.room || '—'],
                ['Floor', selectedBed.floor || '—'],
                ['Type', selectedBed.type],
                ['Center', selectedBed.center?.name || '—'],
              ].map(([label, val]) => (
                <div key={label}>
                  <p className="text-gray-400 text-xs">{label}</p>
                  <p className="font-medium capitalize">{val}</p>
                </div>
              ))}
              <div>
                <p className="text-gray-400 text-xs">Status</p>
                <div className="mt-0.5">{(() => { const cfg = BED_STATUS_CONFIG[selectedBed.status]; return <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>{cfg.label}</span>; })()}</div>
              </div>
            </div>
            {selectedBed.currentPatient && (
              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-xs text-blue-600 font-semibold mb-1">Current Patient</p>
                <p className="text-sm font-medium text-gray-800">{selectedBed.currentPatient.name}</p>
              </div>
            )}
            {selectedBed.features?.length > 0 && (
              <div>
                <p className="text-xs text-gray-400 mb-1.5">Features</p>
                <div className="flex flex-wrap gap-1">{selectedBed.features.map(f => <Badge key={f} color="gray">{f}</Badge>)}</div>
              </div>
            )}
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Update Status</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(BED_STATUS_CONFIG).map(([status, cfg]) => (
                  <button key={status} disabled={updating || selectedBed.status === status}
                    onClick={() => updateBedStatus(selectedBed._id, status)}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all
                      ${selectedBed.status === status ? cfg.color + ' cursor-default' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}>
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>
            {selectedBed.notes && <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2">📝 {selectedBed.notes}</p>}
          </div>
        )}
      </Modal>

      {/* Create Bed Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add New Bed" size="sm">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Bed Number *" value={newBed.bedNumber} onChange={e => setNewBed(f => ({ ...f, bedNumber: e.target.value }))} placeholder="B01" />
            <Input label="Room" value={newBed.room} onChange={e => setNewBed(f => ({ ...f, room: e.target.value }))} placeholder="Room 1" />
            <Input label="Floor" value={newBed.floor} onChange={e => setNewBed(f => ({ ...f, floor: e.target.value }))} placeholder="Ground" />
            <Select label="Type" value={newBed.type} onChange={e => setNewBed(f => ({ ...f, type: e.target.value }))}>
              <option value="therapy">Therapy</option>
              <option value="consultation">Consultation</option>
              <option value="rest">Rest</option>
              <option value="emergency">Emergency</option>
            </Select>
          </div>
          <Select label="Center *" value={newBed.center} onChange={e => setNewBed(f => ({ ...f, center: e.target.value }))}>
            <option value="">Select center</option>
            {centers.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </Select>
          <div className="flex gap-3 justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createBed} loading={saving}>Add Bed</Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
}
